import { animate } from 'framer-motion';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import type { BoardStage } from './landingBoardDemoData';
import { mockPointerClick, sleep } from './landingMockPointerUtils';

export interface Point {
  x: number;
  y: number;
}

export interface OverlayBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type OverlayPhase = 'hidden' | 'lift' | 'drag' | 'land';

interface Measurements {
  cardBox: OverlayBox;
  dropInProgress: OverlayBox;
  dropBacklog: OverlayBox;
}

const TIMING_MS = {
  holdStart: 1200,
  approach: 500,
  drag: 850,
  lift: 380,
  land: 420,
  holdMid: 600,
  hide: 250,
  layoutSettle: 80
} as const;

const COLUMN_GAP_PX = 6;

async function tweenBox(
  from: OverlayBox,
  to: OverlayBox,
  durationMs: number,
  onUpdate: (box: OverlayBox) => void,
  onCursor: (p: Point) => void,
  cancelled: () => boolean
) {
  if (cancelled()) return;
  await new Promise<void>((resolve) => {
    const controls = animate(0, 1, {
      duration: durationMs / 1000,
      ease: [0.45, 0, 0.2, 1],
      onUpdate: (t) => {
        if (cancelled()) {
          controls.stop();
          return;
        }
        const box: OverlayBox = {
          x: from.x + (to.x - from.x) * t,
          y: from.y + (to.y - from.y) * t,
          width: from.width,
          height: from.height
        };
        onUpdate(box);
        onCursor({
          x: box.x + box.width / 2 + 10,
          y: box.y + box.height / 2 + 8
        });
      },
      onComplete: () => {
        if (!cancelled()) resolve();
      }
    });
  });
}

async function tweenPoint(
  from: Point,
  to: Point,
  durationMs: number,
  onUpdate: (p: Point) => void,
  cancelled: () => boolean
) {
  if (cancelled()) return;
  await new Promise<void>((resolve) => {
    const controls = animate(0, 1, {
      duration: durationMs / 1000,
      ease: [0.45, 0, 0.2, 1],
      onUpdate: (t) => {
        if (cancelled()) {
          controls.stop();
          return;
        }
        onUpdate({
          x: from.x + (to.x - from.x) * t,
          y: from.y + (to.y - from.y) * t
        });
      },
      onComplete: () => {
        if (!cancelled()) resolve();
      }
    });
  });
}

function boxCenter(box: OverlayBox): Point {
  return { x: box.x + box.width / 2, y: box.y + box.height / 2 };
}

export function useBoardDragLoop(enabled: boolean) {
  const stageRef = useRef<HTMLDivElement>(null);
  const movingCardRef = useRef<HTMLDivElement>(null);
  const backlogColRef = useRef<HTMLDivElement>(null);
  const inProgressColRef = useRef<HTMLDivElement>(null);

  const [movingStage, setMovingStage] = useState<BoardStage>('Backlog');
  const [showCursor, setShowCursor] = useState(false);
  const [pointerClicking, setPointerClicking] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayPhase, setOverlayPhase] = useState<OverlayPhase>('hidden');
  const [isDragging, setIsDragging] = useState(false);
  const [cursor, setCursor] = useState<Point>({ x: 0, y: 0 });
  const [overlayBox, setOverlayBox] = useState<OverlayBox>({
    x: 0,
    y: 0,
    width: 0,
    height: 0
  });
  const [measureReady, setMeasureReady] = useState(false);

  const cancelledRef = useRef(false);

  const columnDropBox = useCallback(
    (colEl: HTMLDivElement, stageRect: DOMRect, cardRect: DOMRect): OverlayBox => {
      const colBody = colEl.querySelector('.work-items-route__board-col-body');
      const bodyRect = colBody?.getBoundingClientRect() ?? colEl.getBoundingClientRect();
      const cards = colBody?.querySelectorAll('.pn-record-card');
      const anchorCard =
        cards && cards.length > 0 ? (cards[cards.length - 1] as HTMLElement) : null;
      const anchorRect = anchorCard?.getBoundingClientRect() ?? bodyRect;

      const x = anchorCard
        ? anchorRect.left - stageRect.left
        : bodyRect.left - stageRect.left;
      const y =
        anchorCard != null
          ? anchorRect.bottom - stageRect.top + COLUMN_GAP_PX
          : bodyRect.top - stageRect.top;

      return {
        x,
        y,
        width: cardRect.width,
        height: cardRect.height
      };
    },
    []
  );

  const measure = useCallback((): Measurements | null => {
    const stage = stageRef.current;
    const card = movingCardRef.current;
    const backlogCol = backlogColRef.current;
    const inProgressCol = inProgressColRef.current;
    if (!stage || !card || !backlogCol || !inProgressCol) return null;

    const sr = stage.getBoundingClientRect();
    const cr = card.getBoundingClientRect();

    const cardBox: OverlayBox = {
      x: cr.left - sr.left,
      y: cr.top - sr.top,
      width: cr.width,
      height: cr.height
    };

    return {
      cardBox,
      dropInProgress: columnDropBox(inProgressCol, sr, cr),
      dropBacklog: columnDropBox(backlogCol, sr, cr)
    };
  }, [columnDropBox]);

  const runLeg = useCallback(
    async (
      fromStage: BoardStage,
      toStage: BoardStage,
      isCancelled: () => boolean
    ) => {
      setMovingStage(fromStage);
      await sleep(TIMING_MS.layoutSettle, isCancelled);
      if (isCancelled()) return;

      const m = measure();
      if (!m) return;

      const grabBox = m.cardBox;
      const dropBox = toStage === 'In progress' ? m.dropInProgress : m.dropBacklog;
      const center = boxCenter(grabBox);

      setShowCursor(true);
      const hoverOffset = { x: center.x + 14, y: center.y + 10 };
      setCursor(hoverOffset);
      await tweenPoint(hoverOffset, center, TIMING_MS.approach, setCursor, isCancelled);
      if (isCancelled()) return;
      await mockPointerClick(setPointerClicking, isCancelled);
      if (isCancelled()) return;

      setIsDragging(true);
      setShowOverlay(true);
      setOverlayBox(grabBox);
      setOverlayPhase('lift');
      setCursor({ x: center.x + 10, y: center.y + 8 });
      await sleep(TIMING_MS.lift, isCancelled);
      if (isCancelled()) return;

      setOverlayPhase('drag');
      await tweenBox(
        grabBox,
        dropBox,
        TIMING_MS.drag,
        setOverlayBox,
        setCursor,
        isCancelled
      );
      if (isCancelled()) return;

      setOverlayPhase('land');
      await sleep(TIMING_MS.land, isCancelled);
      if (isCancelled()) return;

      setIsDragging(false);
      setShowOverlay(false);
      setOverlayPhase('hidden');
      setMovingStage(toStage);
      await sleep(TIMING_MS.layoutSettle, isCancelled);
    },
    [measure]
  );

  useLayoutEffect(() => {
    if (!enabled) {
      setMeasureReady(false);
      return;
    }
    const id = requestAnimationFrame(() => {
      setMeasureReady(measure() !== null);
    });
    return () => cancelAnimationFrame(id);
  }, [enabled, movingStage, measure]);

  useEffect(() => {
    if (!enabled || !measureReady) return;

    cancelledRef.current = false;
    const isCancelled = () => cancelledRef.current;

    const loop = async () => {
      await sleep(TIMING_MS.holdStart, isCancelled);

      while (!isCancelled()) {
        await runLeg('Backlog', 'In progress', isCancelled);
        if (isCancelled()) break;

        await sleep(TIMING_MS.holdMid, isCancelled);
        if (isCancelled()) break;

        await runLeg('In progress', 'Backlog', isCancelled);
        if (isCancelled()) break;

        setShowCursor(false);
        setPointerClicking(false);
        setIsDragging(false);
        setShowOverlay(false);
        setOverlayPhase('hidden');
        await sleep(TIMING_MS.hide, isCancelled);
        await sleep(TIMING_MS.holdStart, isCancelled);
      }
    };

    void loop();

    return () => {
      cancelledRef.current = true;
    };
  }, [enabled, measureReady, runLeg]);

  useEffect(() => {
    if (!enabled) return;
    let resizeTimer: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setMeasureReady(measure() !== null);
      }, 150);
    };
    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
    };
  }, [enabled, measure]);

  return {
    stageRef,
    movingCardRef,
    backlogColRef,
    inProgressColRef,
    movingStage,
    showCursor,
    pointerClicking,
    showOverlay,
    overlayPhase,
    isDragging,
    cursor,
    overlayBox
  };
}
